import { useCallback, useEffect, useState } from 'react';
import { AppWindow, LayoutGrid, Loader2, Plus, X } from 'lucide-react';
import { apiErrorMessage, captureService, type App, type AppStatus, type CaptureListItem } from '@/services/captureApi';
import { isReplayBuilt } from '@/components/captures/replayStatus';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { IconTipB } from '@/components/studio/IconTipB';

const STATUS: Record<AppStatus, { label: string; classes: string }> = {
  ready: { label: 'Ready', classes: 'text-emerald-600 dark:text-emerald-400' },
  publishing: { label: 'Publishing', classes: 'text-muted-foreground' },
  failed: { label: 'Failed', classes: 'text-destructive' },
};

const hostOf = (url: string) => {
  try { return new URL(url).hostname; } catch { return url; }
};

/** `trigger="icon"`: Direction B's top bar, the apps icon with a dot when the project has apps. */
export function ProjectApps({ projectId, trigger = 'label' }: { projectId: string; trigger?: 'label' | 'icon' }) {
  const [apps, setApps] = useState<App[]>([]);
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    captureService.listProjectApps(projectId).then(setApps, e => setError(apiErrorMessage(e)));
  }, [projectId]);

  useEffect(load, [load]);

  function toggle(next: boolean) {
    setOpen(next);
    if (!next) return;
    setError(null);
    load();
  }

  async function remove(appId: string) {
    setError(null);
    try {
      await captureService.detachApp(projectId, appId);
      setApps(list => list.filter(a => a.id !== appId));
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  function attached(app: App) {
    setApps(list => [...list.filter(a => a.id !== app.id), app]);
    setPicking(false);
  }

  return (
    <>
      <Popover open={open} onOpenChange={toggle}>
        {trigger === 'icon' ? (
          <IconTipB label="Apps">
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={apps.length > 0 ? `Apps, ${apps.length}` : 'Apps'}
                aria-pressed={open}
                className={`relative grid h-8 w-8 place-items-center rounded-[var(--b-radius-control)] transition-colors ${open ? 'bg-b-raised text-b-t1' : 'text-b-t3 hover:bg-b-raised hover:text-b-t1'}`}
              >
                <LayoutGrid className="h-[18px] w-[18px]" />
                {apps.length > 0 && <span aria-hidden="true" className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-b-t3" />}
              </button>
            </PopoverTrigger>
          </IconTipB>
        ) : (
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 gap-1.5 studio-text-secondary hover:studio-text-primary hover:bg-black/5 dark:hover:bg-white/5"
            >
              <AppWindow className="w-4 h-4" />
              <span className="text-xs">Apps</span>
              {apps.length > 0 && (
                <span className="min-w-4 h-4 px-1 rounded-full bg-primary/15 text-primary text-[10px] font-semibold tabular-nums inline-flex items-center justify-center">
                  {apps.length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        )}
        <PopoverContent align="end" className="w-80 p-0">
          <div className="px-3 h-10 border-b border-border flex items-center">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Apps</span>
          </div>
          {apps.length ? (
            <ul className="max-h-72 overflow-y-auto py-1">
              {apps.map(app => (
                <li key={app.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  <AppWindow className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-foreground" title={app.name}>{app.name}</span>
                  <AppStatusLabel app={app} />
                  <button
                    type="button"
                    aria-label={`Remove ${app.name}`}
                    onClick={() => remove(app.id)}
                    className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-4 text-xs text-muted-foreground">No apps yet. Add a recorded app so the agent can film it.</p>
          )}
          {error && <p role="alert" className="px-3 pb-2 text-xs text-destructive">{error}</p>}
          <div className="p-2 border-t border-border">
            <button
              type="button"
              onClick={() => { setOpen(false); setPicking(true); }}
              className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md text-xs font-semibold text-primary hover:bg-primary/10 transition"
            >
              <Plus className="h-3.5 w-3.5" />
              Add from captures
            </button>
          </div>
        </PopoverContent>
      </Popover>
      {picking && <AppPicker projectId={projectId} attached={apps} onAttached={attached} onClose={() => setPicking(false)} />}
    </>
  );
}

function AppStatusLabel({ app }: { app: App }) {
  const { label, classes } = STATUS[app.status];
  return (
    <span title={app.error ?? undefined} className={cn('shrink-0 inline-flex items-center gap-1 text-[11px] font-medium', classes)}>
      {app.status === 'publishing' && <Loader2 className="h-3 w-3 animate-spin" />}
      {label}
    </span>
  );
}

interface Choices {
  apps: App[];
  captures: CaptureListItem[];
}

interface AppPickerProps {
  projectId: string;
  attached: App[];
  onAttached: (app: App) => void;
  onClose: () => void;
}

function AppPicker({ projectId, attached, onAttached, onClose }: AppPickerProps) {
  const [choices, setChoices] = useState<Choices | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([captureService.listApps('ready'), captureService.list(100)]).then(
      ([apps, { captures }]) => {
        if (cancelled) return;
        setChoices({
          apps: apps.filter(a => !attached.some(x => x.id === a.id)),
          captures: captures.filter(c =>
            isReplayBuilt(c.replayStatus) && !apps.some(a => a.captureId === c.id && a.buildId === c.replayBuildId)),
        });
      },
      e => { if (!cancelled) setError(apiErrorMessage(e)); },
    );
    return () => { cancelled = true; };
  }, [attached]);

  async function pick(key: string, toApp: () => Promise<App>) {
    setBusy(key);
    setError(null);
    try {
      const app = await toApp();
      onAttached(await captureService.attachApp(projectId, app.id));
    } catch (e) {
      setError(apiErrorMessage(e));
      setBusy(null);
    }
  }

  const empty = choices && !choices.apps.length && !choices.captures.length;

  return (
    <Dialog open onOpenChange={next => !next && !busy && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AppWindow className="h-4 w-4 text-primary" />
            Add an app from your captures
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-4 py-1">
          {!choices && !error && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading your apps and captures…
            </p>
          )}
          {empty && (
            <p className="text-xs text-muted-foreground">
              Nothing to add yet. Record an app with the capture extension; once its replay is built it shows up here.
            </p>
          )}
          {!!choices?.apps.length && (
            <PickerSection title="Apps">
              {choices.apps.map(app => (
                <PickerRow
                  key={app.id}
                  name={app.name}
                  detail={`Published ${new Date(app.createdAt).toLocaleDateString()}`}
                  busyLabel="Adding…"
                  busy={busy === app.id}
                  disabled={!!busy}
                  onPick={() => pick(app.id, async () => app)}
                />
              ))}
            </PickerSection>
          )}
          {!!choices?.captures.length && (
            <PickerSection title="Captures">
              {choices.captures.map(capture => (
                <PickerRow
                  key={capture.id}
                  name={capture.name}
                  detail={`${hostOf(capture.url)}${capture.replayStatus === 'needs_fix' ? ' · replay needs a fix' : ''}`}
                  busyLabel="Publishing the app…"
                  busy={busy === capture.id}
                  disabled={!!busy}
                  onPick={() => pick(capture.id, () => captureService.publishAppAndWait(capture.id))}
                />
              ))}
            </PickerSection>
          )}
          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PickerSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

interface PickerRowProps {
  name: string;
  detail: string;
  busyLabel: string;
  busy: boolean;
  disabled: boolean;
  onPick: () => void;
}

function PickerRow({ name, detail, busyLabel, busy, disabled, onPick }: PickerRowProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      className="w-full flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left hover:border-primary/50 hover:bg-primary/5 disabled:opacity-60 disabled:pointer-events-none transition"
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{name}</span>
        <span className="block truncate text-[11px] text-muted-foreground">{detail}</span>
      </span>
      {busy ? (
        <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-primary">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {busyLabel}
        </span>
      ) : (
        <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}
